import { cn } from '@/lib/utils'
import { useMedia } from '@/utils/hooks'
import { format as frmt } from '@/utils/date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, addYears, endOfYear, isSameDay } from 'date-fns'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import type {
  CalendarWeek,
  DateRange,
  Matcher,
  Modifiers,
} from 'react-day-picker'
import type { FAIconProps } from '../fa-icon'
import { FAIcon } from '../fa-icon'
import type { CalendarProps } from '../ui'
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../ui/drawer'
import { QuickRanges } from './quick-ranges'
import { getAlternatingRangeValue } from './util'

interface CommonProps {
  id?: string
  className?: string
  triggerClassName?: string

  placeholder?: string
  triggerIcon?: FAIconProps
  disabled?: boolean
  clearable?: boolean
  format?: string
  hideWeekNumbers?: boolean
  disabledDates?: CalendarProps['disabled']
  footer?: ReactNode
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: Date | null
  selectionMode?: never

  disableWeekClick?: false
  hideQuickRanges?: false
  maxRangeDays?: never

  onChange(date: Date | null): void
}

type RangeSelectionMode = 'alternating' | 'continuous'

interface RangeProps extends CommonProps {
  mode: 'range'
  value: DateRange | null
  selectionMode?: RangeSelectionMode

  disableWeekClick?: boolean
  hideQuickRanges?: boolean
  /**
   * Cap the selectable span (inclusive days). Presets longer than this are
   * hidden and, once a start is picked, dates beyond the cap are disabled.
   */
  maxRangeDays?: number

  onChange(dates: DateRange | null): void
}

type Props = SingleProps | RangeProps

/** Normalize a `disabled` value to a matcher list. */
function toMatcherList(matcher: Matcher | Matcher[] | undefined): Matcher[] {
  if (!matcher) return []
  return Array.isArray(matcher) ? matcher : [matcher]
}

export function DatePicker({
  id,
  className,
  triggerClassName,

  mode,
  value,
  selectionMode = 'alternating',

  placeholder,
  triggerIcon,
  disabled = false,
  clearable = false,
  format = 'PPP',
  hideWeekNumbers = false,
  disableWeekClick = false,
  hideQuickRanges = false,
  maxRangeDays,
  disabledDates,
  footer,

  onChange,
}: Props) {
  const translations = useTranslate({
    placeholder: 'common.select-date',
    clear: 'components.date-picker.clear',
  })

  const [open, setOpen] = useState(false)
  const nextRangeBoundaryRef = useRef<'from' | 'to'>('from')
  const [mobile] = useMedia()

  // Remount key for the popover/drawer, bumped when this tree gets hidden.
  const [generation, setGeneration] = useState(0)

  // `<Activity mode="hidden">` (how the views swap without losing filter state)
  // keeps this component mounted, and the calendar lives in a portal that is
  // not hidden along with the rest of the subtree — an open calendar would keep
  // floating over the view the user switched to. Effects are torn down when the
  // tree is hidden, so this cleanup is our "we got hidden" signal. Closing
  // alone isn't enough: the exit transition is effect-driven too, so the portal
  // would be left behind mid-animation. Remounting drops it outright.
  useEffect(() => {
    return () => {
      setOpen(false)
      setGeneration(value => value + 1)
    }
  }, [])

  const label = (() => {
    if (!value) return placeholder ?? translations.placeholder

    if (mode === 'single') return frmt(value, format)

    const { from, to } = value
    if (!from) return placeholder ?? translations.placeholder

    return `${frmt(from, format)} - ${to ? frmt(to, format) : '?'}`
  })()

  function handleWeekClick(week: CalendarWeek) {
    if (disableWeekClick || mode === 'single') return

    const firstDay = week.days.at(0)
    const lastDay = week.days.at(-1)

    if (!firstDay || !lastDay) return
    const firstDate = firstDay.date
    const lastDate = lastDay.date

    onChange({ from: firstDate, to: lastDate })

    if (selectionMode === 'alternating') {
      nextRangeBoundaryRef.current = 'from'
    }
  }

  function handleRangeSelect(
    selected: DateRange | undefined,
    triggerDate: Date,
    modifiers: Modifiers
  ) {
    // This handler only runs in range mode; narrow the union accordingly.
    const current = value as DateRange | null
    const emit = onChange as (next: DateRange | null) => void

    if (selectionMode === 'continuous') {
      // rdp has no resetOnSelect here: clicking a complete range starts over.
      const isRealRange =
        current?.from && current.to && !isSameDay(current.from, current.to)
      emit(
        isRealRange ? { from: triggerDate, to: undefined } : selected ?? null
      )
      return
    }

    if (modifiers.disabled) return

    const boundary = !current?.from
      ? 'from'
      : !current.to
      ? 'to'
      : nextRangeBoundaryRef.current
    const { range, nextBoundary } = getAlternatingRangeValue(
      current,
      triggerDate,
      boundary
    )
    emit(range)
    nextRangeBoundaryRef.current = nextBoundary
  }

  // Cap UI: while picking the end, grey out days beyond ±cap from the start.
  const rangeStart = mode === 'range' ? value?.from : undefined
  const rangeEnd = mode === 'range' ? value?.to : undefined
  const pickingEnd =
    !!rangeStart && (!rangeEnd || isSameDay(rangeStart, rangeEnd))

  const rangeDisabled: Matcher | Matcher[] | undefined =
    maxRangeDays && rangeStart && pickingEnd
      ? [
          ...toMatcherList(disabledDates),
          { before: addDays(rangeStart, -(maxRangeDays - 1)) },
          { after: addDays(rangeStart, maxRangeDays - 1) },
        ]
      : disabledDates

  const triggerButton = (
    <Button
      id={id}
      variant="outline"
      className={cn(
        'w-full min-w-0 justify-between px-4 bg-quaternary border-gray-200 text-base font-normal',
        'disabled:opacity-100',
        open && 'border-secondary hover:border-secondary',
        triggerClassName
      )}
      disabled={disabled}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>

      <div className="flex items-center gap-2 pl-4">
        {clearable && !!value && !disabled && (
          <Button
            variant="outline"
            size="sm"
            onClick={e => {
              e.stopPropagation()
              if (mode === 'range' && selectionMode === 'alternating') {
                nextRangeBoundaryRef.current = 'from'
              }
              onChange(null)
            }}
          >
            {translations.clear}
          </Button>
        )}

        {(!!triggerIcon || !disabled) && (
          <FAIcon
            icon={open ? 'chevron-up' : 'chevron-down'}
            {...triggerIcon}
          />
        )}
      </div>
    </Button>
  )

  const calendarBody =
    mode === 'single' ? (
      <Calendar
        mode="single"
        captionLayout="label"
        selected={value ?? undefined}
        defaultMonth={value ?? undefined}
        startMonth={new Date(1900, 0, 1)}
        endMonth={endOfYear(addYears(new Date(), 5))}
        showWeekNumber={!hideWeekNumbers}
        disabled={disabledDates}
        footer={footer}
        onSelect={value => {
          onChange(
            !value
              ? null
              : new Date(
                  value.getFullYear(),
                  value.getMonth(),
                  value.getDate(),
                  12, // Try to avoid timezone issues with pure dates
                  0
                )
          )
          setOpen(false)
        }}
      />
    ) : (
      <div className="flex flex-col md:flex-row">
        {!hideQuickRanges && (
          <QuickRanges
            value={value}
            onClick={onChange}
            maxRangeDays={maxRangeDays}
          />
        )}

        <Calendar
          mode="range"
          captionLayout="label"
          selected={value ?? undefined}
          defaultMonth={value?.from}
          numberOfMonths={mobile ? 1 : 2}
          startMonth={new Date(2000, 0, 1)}
          endMonth={endOfYear(addYears(new Date(), 5))}
          showWeekNumber={!hideWeekNumbers}
          disabled={rangeDisabled}
          footer={footer}
          onWeekClick={handleWeekClick}
          onSelect={handleRangeSelect}
        />
      </div>
    )

  // On mobile, render the calendar in a bottom sheet instead of a popover.
  // A near-full-width popover anchored to the trigger is what made the picker
  // feel unstable (floating-ui shifting/flipping it and overflowing the edge);
  // a drawer is fixed to the bottom and can't drift.
  if (mobile) {
    return (
      <div className={className}>
        <Drawer key={generation} open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader className="sr-only">
              <DrawerTitle>
                {placeholder ?? translations.placeholder}
              </DrawerTitle>
            </DrawerHeader>
            <div className="flex justify-center overflow-y-auto p-2 pb-6">
              {calendarBody}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    )
  }

  return (
    <div className={className}>
      <Popover key={generation} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          {calendarBody}
        </PopoverContent>
      </Popover>
    </div>
  )
}
